import { motion } from "framer-motion";
import CTASection from "@/sections/CTASection";

const team = [
    {
        name: "Terry Dohrmann",
        role: "CEO & Co-Founder",
        bio: "Co-founder and former CRO at Litify, the legal CMS born inside Morgan & Morgan and acquired by Bessemer Venture Partners. Former Platform VP at Salesforce and Evault Executive. Twenty years of building and selling software to law firms — and now, improving their operations.",
        photo: "https://cdn.prod.website-files.com/69d8175829b57ad97fa23750/69dd4d8d19d2832ec5aa5969_Image1.png",
        linkedin: "https://www.linkedin.com/in/terence-dohrmann-256a794/",
    },
    {
        name: "Rab Govil",
        role: "Executive Chairman",
        bio: "Serial founder behind Naehas and Zenarate, two companies valued at $300M+. Co-founder of AI for All with Dr. Fei-Fei Li. Believes the future of AI is collaborative, not replacing.",
        photo: "https://cdn.prod.website-files.com/69d8175829b57ad97fa23750/69dad3cdf451b41c78851782_image3.png",
        linkedin: "https://www.linkedin.com/in/rabgovil/",
    },
    {
        name: "Tejas Sarvankar",
        role: "Co-founder & Product",
        bio: "Masters in HCI from Carnegie Mellon. Former UX and product lead on the IRIS Lunar Rover mission. Leads product and design for Lexafide's agent platform.",
        photo: "https://cdn.prod.website-files.com/69d8175829b57ad97fa23750/69dd4d9b187899999b67a586_Image22.png",
        linkedin: "https://linkedin.com/in/tejassarvankar",
        hidden: true, // temporarily hidden — flip to false to restore
    },
];

const impact = [
    { value: "60%", label: "Reduction in reception call volume" },
    { value: ">95%", label: "Call routing accuracy" },
    { value: "2-3×", label: "More qualified leads to humans" },
    { value: "60%", label: "Less intake handling time" },
];

const AboutPage = () => {
    return (
        <main data-testid="about-page">
            {/* Hero */}
            <section className="relative pt-28 md:pt-36 pb-20 md:pb-24">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="max-w-4xl"
                    >
                        <span className="eyebrow-pill">About Lexafide</span>
                        <h1 className="font-display text-[clamp(2.4rem,5.4vw,4.4rem)] leading-[1.02] tracking-tight text-[var(--ink)] mt-5 font-semibold">
                            Built so legal teams can do the{" "}
                            <span className="accent-blue">work only they can do.</span>
                        </h1>
                        <p className="mt-6 text-xl text-[var(--ink-soft)] leading-relaxed max-w-2xl">
                            Lexafide is the <span className="accent-blue font-medium">operational intelligence layer</span> for law firms.
                            Automating the conversations, follow-ups, and casework that bury your team.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Mission */}
            <section className="relative py-20 md:py-24 bg-[var(--cream-2)] border-y border-[var(--border)]">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-4"
                    >
                        <span className="eyebrow-pill">Mission</span>
                        <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                            We take the work,{" "}
                            <span className="accent-blue">not the judgment.</span>
                        </h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="lg:col-span-8 space-y-5 text-lg text-[var(--ink-soft)] leading-relaxed"
                    >
                        <p>
                            Lexafide was built around a simple belief:{" "}
                            <span className="text-[var(--ink)] font-medium">legal teams shouldn&apos;t spend half their week on work that doesn&apos;t require a legal practice.</span>{" "}
                            Calls to answer. Follow-ups to chase. Adjusters to corner. Forms to update. Notes to type. Cases to log.
                        </p>
                        <p>
                            The firms we serve aren&apos;t faceless companies. They&apos;re founders and partners — lawyers whose names are on the building, on case files, and every promise the firm makes. A reputation like that is built one case at a time, and it doesn&apos;t get handed to a black box. So we don&apos;t build black boxes. We build agents you can{" "}
                            <span className="text-[var(--ink)] font-medium">hear, audit, and override</span>{" "}
                            — and we earn the next workflow by delivering the first one.
                        </p>
                        <p>
                            We watched plaintiff firms hire more paralegals, buy more tools, and rebuild the same intake processes again and again —
                            and the real bottleneck stayed untouched. Nobody had time to actually be present with the clients who needed them.
                        </p>
                        <p>
                            So we built a platform that takes the work, not the judgment. We{" "}
                            <span className="accent-blue font-medium">handcraft AI agents</span> for your firm — autonomous when speed matters,
                            alongside your team when it doesn&apos;t. Every conversation captured, every case file complete, every adjuster chased,
                            every lead followed up. Built on top of your telephony and case management system, tuned to your workflows, shaped to your firm&apos;s voice.
                        </p>
                        <p className="text-[var(--ink)] font-medium">
                            No generic chatbot. No forced templates. No replacing the people who make your firm work.
                            Just clarity, capacity, and the time to do the work only legal professionals can do.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Impact metrics */}
            <section className="relative py-20 md:py-24">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="mb-12"
                    >
                        <span className="eyebrow-pill">Impact in production</span>
                        <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-tight font-semibold text-[var(--ink)] mt-5 max-w-3xl">
                            Numbers from firms running Lexafide today.
                        </h2>
                    </motion.div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                        {impact.map((m, i) => (
                            <motion.div
                                key={m.label}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                data-testid={`about-impact-${i}`}
                                className="card-paper p-6 md:p-7"
                            >
                                <div className="font-display text-[clamp(2.6rem,4.8vw,3.6rem)] font-semibold text-[var(--ink)] tabular-nums leading-none">
                                    {m.value}
                                </div>
                                <div className="text-sm text-[var(--ink-soft)] mt-3 leading-snug">{m.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* A note from our CEO */}
            <section
                data-testid="ceo-note-section"
                className="relative py-20 md:py-28 bg-[var(--cream-2)] border-t border-[var(--border)]"
            >
                <div className="max-w-[920px] mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="eyebrow-pill" data-testid="ceo-note-eyebrow">
                            A note from our CEO
                        </span>
                        <div className="mt-8 space-y-5 text-[1.075rem] md:text-[1.15rem] leading-[1.7] text-[var(--ink-soft)] font-display font-normal">
                            <p>
                                I&apos;ve spent twenty years building and selling software to law firms — most recently as co-founder and CRO of Litify, the case management platform that grew up inside Morgan &amp; Morgan and now runs hundreds of plaintiff firms in the country.
                            </p>
                            <p>
                                In all that time, one pattern never changed: the firms that win aren&apos;t the ones with the most tools. They&apos;re the ones whose operations don&apos;t leak —{" "}
                                <span className="text-[var(--ink)] font-medium">no missed call, no stalled file, no client wondering if anyone&apos;s working their case.</span>
                            </p>
                            <p>
                                That&apos;s what we build at Lexafide. Not a product you install, but an operations partner that learns how your firm works and carries the load your team shouldn&apos;t have to.
                            </p>
                            <p className="text-[var(--ink)] font-medium">
                                Your name is on the door. Ours is on the work.
                            </p>
                        </div>

                        {/* Signature block */}
                        <div className="mt-10 flex items-center gap-4">
                            <div
                                data-testid="ceo-note-headshot"
                                data-placeholder="terry-headshot"
                                className="relative w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[var(--border)]"
                            >
                                <img
                                    src="https://cdn.prod.website-files.com/69d8175829b57ad97fa23750/69dd4d8d19d2832ec5aa5969_Image1.png"
                                    alt="Terry Dohrmann"
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover object-center"
                                />
                            </div>
                            <div>
                                <div className="font-display text-lg text-[var(--ink)] font-semibold leading-tight">Terry Dohrmann</div>
                                <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--muted)] mt-1">
                                    CEO &amp; Co-founder
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Team */}
            <section className="relative py-20 md:py-28 bg-[var(--cream-2)] border-b border-[var(--border)]">
                <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="mb-12 md:mb-16 max-w-3xl"
                    >
                        <span className="eyebrow-pill">Team</span>
                        <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] font-semibold text-[var(--ink)] mt-5">
                            Built by people who&apos;ve shipped{" "}
                            <span className="accent-blue">legal and enterprise software</span> at scale.
                        </h2>
                    </motion.div>
                    {/* Terry — featured (larger, photo + text side-by-side) */}
                    {(() => {
                        const m = team[0];
                        return (
                            <motion.div
                                key={m.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6 }}
                                whileHover={{ y: -4 }}
                                data-testid="team-card-0"
                                className="card-paper p-5 md:p-8 grid grid-cols-1 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-6 md:gap-10 items-center overflow-hidden mb-6 md:mb-8"
                            >
                                <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-[var(--cream)]">
                                    <img
                                        src={m.photo}
                                        alt={m.name}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover object-center grayscale-[0.15] transition-transform duration-700 ease-out hover:scale-[1.03]"
                                    />
                                    <a
                                        href={m.linkedin}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`${m.name} on LinkedIn`}
                                        data-testid="team-linkedin-0"
                                        className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-[var(--ink)] hover:bg-[var(--blue-deep)] hover:text-white transition-colors duration-300 shadow-sm"
                                    >
                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                            <path d="M13.2807 13.281H11.0582V9.80031C11.0582 8.9703 11.0434 7.90183 9.90225 7.90183C8.74467 7.90183 8.56758 8.80616 8.56758 9.73988V13.2808H6.34507V6.12306H8.47871V7.10123H8.50858C8.94365 6.35735 9.75257 5.91305 10.6137 5.945C12.8664 5.945 13.2817 7.42672 13.2817 9.35438L13.2807 13.281ZM3.83727 5.14464C3.12495 5.14478 2.5474 4.56739 2.54726 3.85508C2.54713 3.14273 3.12449 2.56517 3.83678 2.56503C4.54911 2.56489 5.12665 3.14228 5.12679 3.85459C5.12692 4.56694 4.54959 5.14454 3.83727 5.14464ZM4.94852 13.281H2.72369V6.12306H4.94852V13.281ZM14.3887 0.501033H1.60686C1.00277 0.494207 0.50738 0.978113 0.5 1.58222V14.4176C0.507137 15.022 1.00246 15.5064 1.60686 15.4999H14.3887C14.9944 15.5075 15.4916 15.0231 15.5 14.4176V1.58132C15.4914 0.976 14.994 0.492128 14.3887 0.500097V0.501033Z" />
                                        </svg>
                                    </a>
                                </div>
                                <div>
                                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--blue)] font-semibold">
                                        Founder · CEO
                                    </span>
                                    <h3 className="font-display text-[clamp(1.6rem,2.6vw,2.1rem)] text-[var(--ink)] font-semibold leading-tight mt-2">
                                        {m.name}
                                    </h3>
                                    <p className="text-[11px] font-mono tracking-[0.18em] text-[var(--muted)] uppercase mt-2 mb-5">
                                        {m.role}
                                    </p>
                                    <p className="text-[16px] md:text-[17px] text-[var(--ink-soft)] leading-relaxed">
                                        {m.bio}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })()}

                    {/* Other team members (hidden entries filtered out) */}
                    {(() => {
                        const others = team.slice(1).filter((m) => !m.hidden);
                        if (others.length === 0) return null;
                        return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                                {others.map((m, offset) => {
                                    const i = offset + 1;
                                    return (
                                <motion.div
                                    key={m.name}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.6, delay: offset * 0.08 }}
                                    whileHover={{ y: -4 }}
                                    data-testid={`team-card-${i}`}
                                    className="card-paper p-5 md:p-6 flex flex-col overflow-hidden"
                                >
                                    <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden mb-6 bg-[var(--cream)]">
                                        <img
                                            src={m.photo}
                                            alt={m.name}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover object-center grayscale-[0.15] transition-transform duration-700 ease-out hover:scale-[1.03]"
                                        />
                                        <a
                                            href={m.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`${m.name} on LinkedIn`}
                                            data-testid={`team-linkedin-${i}`}
                                            className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center text-[var(--ink)] hover:bg-[var(--blue-deep)] hover:text-white transition-colors duration-300 shadow-sm"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                <path d="M13.2807 13.281H11.0582V9.80031C11.0582 8.9703 11.0434 7.90183 9.90225 7.90183C8.74467 7.90183 8.56758 8.80616 8.56758 9.73988V13.2808H6.34507V6.12306H8.47871V7.10123H8.50858C8.94365 6.35735 9.75257 5.91305 10.6137 5.945C12.8664 5.945 13.2817 7.42672 13.2817 9.35438L13.2807 13.281ZM3.83727 5.14464C3.12495 5.14478 2.5474 4.56739 2.54726 3.85508C2.54713 3.14273 3.12449 2.56517 3.83678 2.56503C4.54911 2.56489 5.12665 3.14228 5.12679 3.85459C5.12692 4.56694 4.54959 5.14454 3.83727 5.14464ZM4.94852 13.281H2.72369V6.12306H4.94852V13.281ZM14.3887 0.501033H1.60686C1.00277 0.494207 0.50738 0.978113 0.5 1.58222V14.4176C0.507137 15.022 1.00246 15.5064 1.60686 15.4999H14.3887C14.9944 15.5075 15.4916 15.0231 15.5 14.4176V1.58132C15.4914 0.976 14.994 0.492128 14.3887 0.500097V0.501033Z" />
                                            </svg>
                                        </a>
                                    </div>
                                    <h3 className="font-display text-xl text-[var(--ink)] font-semibold leading-tight">{m.name}</h3>
                                    <p className="text-[11px] font-mono tracking-[0.18em] text-[var(--muted)] uppercase mt-1.5 mb-4">
                                        {m.role}
                                    </p>
                                    <p className="text-[15px] text-[var(--ink-soft)] leading-relaxed">{m.bio}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                        );
                    })()}
                </div>
            </section>

            {/* Investors section removed per request */}

            <CTASection />
        </main>
    );
};

export default AboutPage;
